<template>
  <Paginator
    :response="membersPage ?? undefined"
    v-slot="{ results }"
    :loading="loading"
    allow-empty
    :page="pageNumber"
    v-on:update:page="(page) => $emit('update:pageNumber', page)"
    singular="member"
    plural="members"
  >
    <table class="table is-fullwidth is-hoverable">
      <thead>
        <tr>
          <th>Name</th>
          <th>
            <template v-if="includeGroups">Email or Identifier</template>
            <template v-else>Email</template>
          </th>
          <th>Role</th>
          <th class="is-narrow">Actions</th>
        </tr>
      </thead>
      <tbody>
        <Member
          v-for="member in results"
          :key="member.id"
          :member="member"
          :is-admin="isAdmin"
          v-on:update="updateMember"
        />
        <AddMemberForm
          :is-admin="isAdmin"
          :content-type="contentType"
          :content-id="contentId"
          :include-groups="includeGroups"
          v-on:update="updatePage(page)"
        />
      </tbody>
    </table>
  </Paginator>
</template>

<script lang="ts">
import { isAxiosError } from 'axios'
import { mapActions, mapState } from 'pinia'
import { type PropType, defineComponent } from 'vue'

import type { GenericMembershipParameters, MembershipContentType } from '@/api'
import Paginator from '@/components/Paginator.vue'
import { ROLES } from '@/config'
import { errorParser, getRole } from '@/helpers'
import {
  useBudgetStore,
  useCorporaStore,
  useNotificationStore,
  useRightsStore,
  useSecretStore,
} from '@/stores'
import type { UUID } from '@/types'

import AddMemberForm from './AddMemberForm.vue'
import Member from './Member.vue'

export default defineComponent({
  components: {
    Paginator,
    Member,
    AddMemberForm,
  },
  emits: ['update:pageNumber'],
  props: {
    contentType: {
      type: String as PropType<MembershipContentType>,
      required: true,
    },
    contentId: {
      type: String as PropType<UUID>,
      required: true,
    },
    includeGroups: {
      // Allow to list and add group members
      type: Boolean,
      default: true,
    },
    pageNumber: {
      // Optional page sync instead of using a query parameter
      type: Number as PropType<number | null>,
      default: null,
    },
  },
  data: () => ({
    loading: false,
  }),
  computed: {
    ...mapState(useRightsStore, ['membersPage', 'groups']),
    ...mapState(useCorporaStore, ['corpora']),
    ...mapState(useBudgetStore, ['budgets']),
    ...mapState(useSecretStore, ['secrets']),
    page(): number {
      return this.pageNumber || parseInt(this.$route.query.page?.toString() ?? '', 10) || 1
    },
    isAdmin() {
      if (this.contentType === 'group') {
        const group = this.groups[this.contentId]
        return group && getRole(group.level) === ROLES.admin
      } else if (this.contentType === 'corpus') {
        const corpus = this.corpora[this.contentId]
        return corpus && corpus.rights.includes('admin')
      } else if (this.contentType === 'budget') {
        const budget = this.budgets[this.contentId]
        return budget && 'rights' in budget && budget.rights.includes('admin')
      } else if (this.contentType === 'secret') {
        const secret = Object.values(this.secrets).find(secret => secret.id === this.contentId)
        return secret && 'rights' in secret && secret.rights.includes('admin')
      }
      // By default return true to display commands
      return true
    },
  },
  methods: {
    ...mapActions(useRightsStore, ['listMembers']),
    ...mapActions(useNotificationStore, ['notify']),
    async updateMember() {
      this.updatePage(this.page, true)
    },
    async updatePage(page: number, handleRedirect = false) {
      this.loading = true
      try {
        // This uses a cast because TS infers { [contentType: string]: string } instead of { [contentType: MembershipContentType]: string }
        const payload: GenericMembershipParameters = {
          [this.contentType]: this.contentId,
        } as unknown as GenericMembershipParameters

        if (page) payload.page = page
        if (!this.includeGroups) payload.type = 'user'

        await this.listMembers(payload)
      } catch (err) {
        if (
          handleRedirect &&
          isAxiosError(err) &&
          err.response &&
          [403, 404].includes(err.response.status)
        ) {
          // User probably removed their own access, redirect depending on the content type
          const route = { name: 'corpus-list' }
          if (this.contentType === 'group') route.name = 'user-profile'
          this.$router.replace(route)
        } else {
          this.notify({
            type: 'error',
            text: `An error occurred listing members: ${errorParser(err)}`,
          })
        }
      } finally {
        this.loading = false
      }
    },
  },
  watch: {
    page: {
      immediate: true,
      handler(newValue) {
        // Only update the page through this watcher if we are using pageNumber and not a page number from the $route
        if (this.pageNumber !== null && this.pageNumber !== undefined) this.updatePage(newValue)
      },
    },
    $route: {
      immediate: true,
      handler(newRoute, oldRoute) {
        // Ignore changes to a different route
        if (oldRoute && oldRoute.path !== newRoute.path) return
        // Ignore this watcher if the pageNumber prop is in use
        if (this.pageNumber !== null && this.pageNumber !== undefined) return
        this.updatePage(newRoute.query.page ?? 1)
      },
    },
    contentId() {
      this.updatePage(1)
    },
    contentType() {
      this.updatePage(1)
    },
  },
})
</script>
